<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import type { RuleViolationError } from '@/types/rule'

import { eventService } from '@/services/eventService'
import { getApiErrorMessage } from '@/utils/apiError'

definePage({
  meta: {
    title: 'pages.offer.registrationPage.guest.confirmationPageTitle',
    requiresAuth: false,
  },
})

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { setBreadcrumbs, setPageTitle } = useBreadcrumbs()

const idOrLink = computed(() => (route.params as { id: string }).id)
const guestConfirmationToken = computed(
  () => (route.query.guestConfirmationToken as string) || undefined,
)
const confirmationSessionKey = computed(() => `guestRegistrationConfirmed:${idOrLink.value}`)

const loading = ref(true)
const confirmed = ref(false)
const errorMessage = ref('')

function getErrorMessage(error: unknown) {
  const ruleViolation = error as RuleViolationError
  if (ruleViolation?.failedRestrictions) {
    return ruleViolation.errorKey
      ? t(ruleViolation.errorKey)
      : ruleViolation.error || t('pages.offer.registrationPage.guest.confirmationError')
  }

  return getApiErrorMessage(error, t, t('pages.offer.registrationPage.guest.confirmationError'))
}

async function confirmGuestRegistration() {
  setPageTitle('pages.offer.registrationPage.guest.confirmationPageTitle')
  setBreadcrumbs([
    { label: 'pages.offer.title', route: '/offer' },
    { label: 'pages.offer.registrationPage.guest.confirmationPageTitle' },
  ])

  if (!guestConfirmationToken.value && sessionStorage.getItem(confirmationSessionKey.value)) {
    confirmed.value = true
    loading.value = false
    return
  }

  const eventId = Number(idOrLink.value)
  if (!Number.isInteger(eventId) || !guestConfirmationToken.value) {
    errorMessage.value = t('pages.offer.registrationPage.guest.invalidConfirmationLink')
    loading.value = false
    return
  }

  try {
    await eventService.confirmGuestRegistration(eventId, {
      token: guestConfirmationToken.value,
    })
    sessionStorage.setItem(confirmationSessionKey.value, 'true')
    confirmed.value = true
    await router.replace({
      path: route.path,
    })
  } catch (error) {
    errorMessage.value = getErrorMessage(error)
  } finally {
    loading.value = false
  }
}

function goBackToEvent() {
  router.push(`/event/${idOrLink.value}`)
}

function goToOffer() {
  router.push('/offer')
}

onMounted(confirmGuestRegistration)
</script>

<template>
  <div class="mx-auto w-full max-w-2xl px-4 py-10">
    <UCard class="border border-neutral-200 shadow-none dark:border-neutral-700">
      <template #default>
        <div class="flex flex-col gap-5">
          <div v-if="loading" class="flex flex-col items-center gap-4 py-8 text-center">
            <AppSpinner />
            <div>
              <h1 class="mb-2 text-2xl font-semibold text-neutral-900 dark:text-white">
                {{ t('pages.offer.registrationPage.guest.confirming') }}
              </h1>
              <p class="m-0 text-neutral-600 dark:text-neutral-300">
                {{ t('pages.offer.registrationPage.guest.confirmationPageLoading') }}
              </p>
            </div>
          </div>

          <template v-else-if="confirmed">
            <AppAlert color="success" :closable="false">
              <div class="font-semibold">
                {{ t('pages.offer.registrationPage.guest.confirmed') }}
              </div>
              <div class="mt-1">
                {{ t('pages.offer.registrationPage.guest.confirmedDescription') }}
              </div>
            </AppAlert>

            <div class="flex flex-wrap justify-end gap-3">
              <UButton
                color="neutral"
                :label="t('pages.offer.registrationPage.backToEvent')"
                icon="i-lucide-arrow-left"
                @click="goBackToEvent"
              />
              <UButton :label="t('pages.offer.title')" icon="i-lucide-list" @click="goToOffer" />
            </div>
          </template>

          <template v-else>
            <AppAlert color="error" :closable="false">
              <div class="font-semibold">
                {{ t('pages.offer.registrationPage.guest.confirmationPageErrorTitle') }}
              </div>
              <div class="mt-1">
                {{ errorMessage }}
              </div>
            </AppAlert>

            <div class="flex flex-wrap justify-end gap-3">
              <UButton
                color="neutral"
                :label="t('pages.offer.registrationPage.backToEvent')"
                icon="i-lucide-arrow-left"
                @click="goBackToEvent"
              />
              <UButton :label="t('pages.offer.title')" icon="i-lucide-list" @click="goToOffer" />
            </div>
          </template>
        </div>
      </template>
    </UCard>
  </div>
</template>
